import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { config } from "@/config/environment";
import { useUserContext } from "@/context/user-context";
import { getReturnUrl } from "@/utilities/msal-utils";

/**
 * Component that handles automatic redirection after successful login
 * This component should be used in the login page to redirect users
 * to their appropriate dashboard based on their roles
 */
export function LoginRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, activeAccount } = useAuth();
  const {
    user,
    getRedirectRoute,
    isLoading: userDetailsLoading
  } = useUserContext();

  useEffect(() => {
    if (config.debug.enabled) {
      console.log("🔍 LoginPage: Main useEffect triggered", {
        isAuthenticated,
        user: !!user,
        getRedirectRoute: !!getRedirectRoute,
        isLoading,
        userDetailsLoading,
        activeAccount: !!activeAccount,
        location: location.pathname,
        search: location.search
      });
    }

    // Only proceed if user is authenticated, has active account, and not loading user details
    if (
      isAuthenticated &&
      activeAccount &&
      !isLoading &&
      !userDetailsLoading &&
      user &&
      getRedirectRoute
    ) {
      if (config.debug.enabled) {
        console.log("🔄 LoginPage: User authenticated, checking for redirect");
      }

      if (user.registrationRequired) {
        if (location.pathname !== "/") {
          navigate("/", { replace: true });
        }
        return;
      }

      // Get the redirect route first
      const redirectRoute = getRedirectRoute();

      // If no redirect route or already on the correct route, don't redirect
      if (!redirectRoute || location.pathname === redirectRoute) {
        if (config.debug.enabled) {
          console.log(
            "✅ LoginPage: Already on correct route or no redirect needed",
            { currentPath: location.pathname, redirectRoute }
          );
        }
        return;
      }

      if (config.debug.enabled) {
        console.log("🔄 LoginPage: Redirect route determined:", redirectRoute);
      }

      // Check for return URL parameter (for deep linking)
      const returnUrl = getReturnUrl();
      if (returnUrl) {
        navigate(returnUrl, { replace: true });
        return;
      }

      // Use role-based redirect
      if (config.debug.enabled) {
        console.log(
          "🚀 LoginPage: Redirecting to role-based route:",
          redirectRoute
        );
      }

      navigate(redirectRoute, { replace: true });
    }
  }, [
    isAuthenticated,
    activeAccount,
    user,
    getRedirectRoute,
    isLoading,
    navigate,
    location.search,
    location.pathname,
    userDetailsLoading
  ]);

  // Show loading state while user details are being fetched
  if (isAuthenticated && isLoading) {
    if (config.debug.enabled) {
      console.log(
        "⏳ LoginRedirect: User authenticated but details still loading..."
      );
    }
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">
            Loading your dashboard...
          </p>
        </div>
      </div>
    );
  }

  // This component doesn't render anything when not loading
  return null;
}
